import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import LandingHeader from '../components/organisms/LandingHeader';
import Button from '../components/atoms/Button';
import Badge from '../components/atoms/Badge';
import Icon from '../components/atoms/Icon';
import Avatar from '../components/atoms/Avatar';
import { applyToJob, getJobDetails, getApplications, updateJobStatus, getCandidateProfile } from '../services/api';
import SessionManager from '../utils/sessionManager';
import CreditCostBadge from '../components/molecules/CreditCostBadge';
import useCredits from '../hooks/useCredits';
import { API_BASE_URL } from '../config';

// Build headers with JWT Authorization from SessionManager
const buildAuthHeaders = (extra = {}) => {
  let session = SessionManager.getCurrentSession();
  if (!session || !session.access_token) {
    session = SessionManager.getSession('candidate') || SessionManager.getSession('hr');
  }
  const token = session?.access_token;
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const JobDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userRole, setUserRole] = useState(null);
  const [applyForm, setApplyForm] = useState({
    candidateName: '',
    candidateEmail: '',
    resumeText: '',
    resumeUrl: '',
    coverLetter: '',
  });
  const [applyState, setApplyState] = useState({ loading: false, message: '', variant: 'neutral' });
  const [applications, setApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [existingApplication, setExistingApplication] = useState(null);
  const [checkingApplication, setCheckingApplication] = useState(false);

  // Credit system
  const { balance, hasEnoughCredits, getCost, deductCreditsForService } = useCredits();
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [isProfileComplete, setIsProfileComplete] = useState(true);
  const [profileData, setProfileData] = useState(null);

  useEffect(() => {
    // Try to migrate legacy session first
    SessionManager.migrateLegacySession();
    
    // Get user role from new session system
    const currentSession = SessionManager.getCurrentSession();
    const role = currentSession?.role;
    setUserRole(role);
    
    // Pre-fill candidate information if logged in as candidate
    if (role === 'candidate') {
      const candidateSession = SessionManager.getSession('candidate');
      const candidateId = candidateSession?.user_id;
      const email = candidateSession?.email;
      
      // Set email from session
      if (email) {
        setApplyForm(prev => ({ ...prev, candidateEmail: email }));
      }
      
      // Load candidate profile to get name
      if (candidateId || email) {
        loadCandidateProfile(candidateId, email);
      }
      
      // Check profile completion
      checkProfileCompletion();
    }
  }, []);

  const checkProfileCompletion = async () => {
    const session = SessionManager.getCurrentSession();
    if (!session) return;

    try {
      const response = await fetch(`${API_BASE_URL}/candidates/${session.user_id}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        const profile = data.profile || data;
        setProfileData(profile);
        
        // Profile is complete if has full_name, phone, and at least one detail section
        const hasBasicInfo = profile.full_name && profile.phone;
        const hasDetails = (profile.education && profile.education.length > 0) ||
                          (profile.work_experience && profile.work_experience.length > 0) ||
                          (profile.skills && profile.skills.length > 0);
        
        setIsProfileComplete(hasBasicInfo && hasDetails);
      }
    } catch (error) {
      console.error('Error checking profile:', error);
    }
  };

  const loadCandidateProfile = async (candidateId, email) => {
    try {
      const profile = await getCandidateProfile(candidateId, email);
      if (profile) {
        const fullName = profile.full_name || `${profile.first_name || ''} ${profile.last_name || ''}`.trim();
        setApplyForm(prev => ({
          ...prev,
          candidateName: fullName || prev.candidateName,
          candidateEmail: profile.email || prev.candidateEmail,
          resumeUrl: profile.resume_url || prev.resumeUrl,
        }));
      }
    } catch (err) {
      console.error('Failed to load candidate profile:', err);
      // Continue with form as is if profile load fails
    }
  };

  useEffect(() => {
    let isMounted = true;
    async function loadJob() {
      setLoading(true);
      setError('');
      try {
        const data = await getJobDetails(id);
        if (isMounted) {
          setJob(data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message);
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadJob();
    return () => {
      isMounted = false;
    };
  }, [id]);

  // Check if candidate already applied to this job
  useEffect(() => {
    const checkExistingApplication = async () => {
      const candidateSession = SessionManager.getSession('candidate');
      if (!candidateSession || !candidateSession.user_id || !job) return;

      setCheckingApplication(true);
      try {
        const candidateId = candidateSession.user_id;
        const { API_BASE_URL } = await import('../config');
        const response = await fetch(`${API_BASE_URL}/candidates/${candidateId}/applications`, { headers: buildAuthHeaders() });
        
        if (response.ok) {
          const data = await response.json();
          const applications = data.applications || [];
          
          // Find application for this job
          const existingApp = applications.find(app => app.job_id === job.id);
          
          if (existingApp) {
            console.log('✅ Found existing application for this job:', existingApp);
            setExistingApplication(existingApp);
          }
        }
      } catch (error) {
        console.error('Error checking existing application:', error);
      } finally {
        setCheckingApplication(false);
      }
    };

    checkExistingApplication();
  }, [job]);

  useEffect(() => {
    // Load applications if HR user
    if (userRole === 'hr' && job) {
      loadApplications();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userRole, job]);

  const loadApplications = async () => {
    if (!job) return;
    setLoadingApplications(true);
    try {
      const data = await getApplications({ job_id: job.id });
      setApplications(data);
    } catch (err) {
      console.error('Failed to load applications:', err);
      setApplications([]);
    } finally {
      setLoadingApplications(false);
    }
  };

  const handleToggleJobStatus = async () => {
    if (!job) return;
    const newStatus = job.status === 'Active' ? 'Closed' : 'Active';
    try {
      await updateJobStatus(job.id, newStatus);
      setJob({ ...job, status: newStatus });
    } catch (err) {
      console.error('Failed to update job status:', err);
    }
  };

  const formatDate = (value) => {
    if (!value) return '—';
    const date = new Date(value);
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleApply = async (event) => {
    event.preventDefault();

    // Check if user is logged in using new session system
    const candidateSession = SessionManager.getSession('candidate');
    const isLoggedInAsCandidate = SessionManager.isLoggedIn('candidate');

    // If not logged in as candidate, bounce to login and come back to this
    // job page after they sign in so they can continue applying.
    if (!isLoggedInAsCandidate || !candidateSession) {
      const returnTo = `${window.location.pathname}${window.location.search}`;
      navigate(`/login?redirect=${encodeURIComponent(returnTo)}`);
      return;
    }
    
    if (!job) return;

    // Check credits before applying
    if (!hasEnoughCredits('job_apply')) {
      setShowCreditModal(true);
      return;
    }

    setApplyState({ loading: true, message: '', variant: 'neutral' });
    try {
      // Deduct credits first
      const creditResult = await deductCreditsForService('job_apply');
      if (!creditResult.success) {
        setShowCreditModal(true);
        setApplyState({ loading: false, message: '', variant: 'neutral' });
        return;
      }

      // Get candidate_id from session (set during login)
      const candidateId = candidateSession.user_id;
      const email = candidateSession.email || applyForm.candidateEmail;
      
      // Ensure we have the latest resume from profile
      let resumeUrl = applyForm.resumeUrl;
      if (!resumeUrl && (candidateId || email)) {
        try {
          const profile = await getCandidateProfile(candidateId, email);
          if (profile?.resume_url) {
            resumeUrl = profile.resume_url;
            setApplyForm((prev) => ({ ...prev, resumeUrl }));
          }
        } catch (err) {
          console.error('Failed to fetch resume from profile:', err);
        }
      }

      if (!resumeUrl) {
        setApplyState({
          loading: false,
          message: '',
          variant: 'neutral',
        });
        setShowResumeModal(true);
        return;
      }
      
      const payload = {
        candidate_id: candidateId || email || 'anonymous', // Use candidate_id from login, fallback to email
        candidate_name: applyForm.candidateName,
        candidate_email: email || applyForm.candidateEmail,
        resume_url: resumeUrl || undefined, // Always include resume from profile if available
        resume_text: applyForm.resumeText || undefined,
        cover_letter: applyForm.coverLetter || undefined,
      };
      const response = await applyToJob(job.id, payload);
      
      // Redirect to match result page with application ID
      if (response && response.id) {
        navigate('/match-result', { state: { applicationId: response.id } });
      } else {
        setApplyState({
          loading: false,
          message: 'Application submitted successfully! The HR team will review your application.',
          variant: 'success',
        });
      }
    } catch (err) {
      setApplyState({
        loading: false,
        message: err.message || 'Unable to submit application.',
        variant: 'error',
      });
    }
  };

  const jobTags = job?.tags || [];
  const jobPdfUrl = job?.jd_pdf_url || job?.jdPdfUrl || job?.jd_pdf || job?.jdPdf;
  const postedDate = job ? formatDate(job.posted_date) : '—';

  return (
    <>
    <div className="relative flex h-auto min-h-screen w-full flex-col overflow-x-hidden">
      <LandingHeader />
      
      <main className="layout-container flex h-full grow flex-col">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="layout-content-container flex flex-col w-full flex-1">
            <div className="flex flex-wrap gap-2 pb-6">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="text-text-light-secondary dark:text-text-dark-secondary text-sm font-medium leading-normal hover:text-primary"
              >
                Back
              </button>
              <span className="text-text-light-secondary dark:text-text-dark-secondary text-sm font-medium leading-normal">/</span>
              <span className="text-text-light-primary dark:text-text-dark-primary text-sm font-medium leading-normal truncate">
                {job?.title || 'Job Details'}
              </span>
            </div>

            {loading ? (
              <div className="flex min-h-[300px] items-center justify-center text-text-secondary-light dark:text-text-secondary-dark">
                Loading job...
              </div>
            ) : error ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-700">
                {error}
              </div>
            ) : !job ? (
              <div className="rounded-xl border border-border-light dark:border-border-dark bg-panel-light dark:bg-panel-dark p-6 text-text-secondary-light dark:text-text-secondary-dark">
                Job not found.
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Left Column: Main Content */}
                <div className="lg:col-span-2 flex flex-col gap-8">
                  {/* Page Heading */}
                  <div className="flex flex-col gap-4 p-6 bg-card-light dark:bg-card-dark rounded-xl shadow-soft">
                    <h1 className="text-text-light-primary dark:text-text-dark-primary text-3xl md:text-4xl font-black leading-tight tracking-[-0.033em]">
                      {job.title}
                    </h1>
                    <p className="text-text-light-secondary dark:text-text-dark-secondary text-base font-normal leading-normal">
                      {job.company} • {job.location} • {job.type}
                    </p>
                  </div>
                  
                  {/* Job Details Card */}
                  <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft overflow-hidden">
                    <div className="pb-3">
                      <div className="border-b border-border-light dark:border-border-dark px-6">
                        <p className="text-sm font-bold leading-normal tracking-[0.015em] pb-[13px] pt-4 text-text-light-primary dark:text-text-dark-primary">
                          Description
                        </p>
                      </div>
                    </div>
                    
                    <div className="p-6 space-y-4 text-text-light-secondary dark:text-text-dark-secondary text-sm leading-relaxed">
                      {(() => {
                        const desc = job.description || '';

                        // Extract inline metadata (Location, Duration, Stipend, Salary) before section parsing
                        const metaPattern = /(?:^|\s)(Location|Duration|Stipend|Salary)[:\s|]+([^.]*?)(?=\s+(?:About|Responsibilities|Key Responsibilities|Requirements|Qualifications|Benefits|Who|What)|$)/gi;
                        const metaItems = [];
                        let metaMatch;
                        while ((metaMatch = metaPattern.exec(desc)) !== null) {
                          metaItems.push({ label: metaMatch[1], value: metaMatch[2].trim() });
                        }

                        // Remove metadata from description for section parsing
                        let cleanDesc = desc;
                        metaItems.forEach(m => {
                          cleanDesc = cleanDesc.replace(new RegExp(m.label + '[:\\s|]+' + m.value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), '');
                        });

                        // Split by section headers (exclude metadata fields)
                        const sectionPattern = /(About the Role|About Us|Responsibilities|Key Responsibilities|What You['']ll Do|Requirements|Qualifications|Required Skills|Nice to Have|Preferred|Benefits|Perks|Who We['']re Looking For|What We Offer)[:\s]*/gi;
                        const parts = cleanDesc.split(sectionPattern);

                        // Build sections
                        const sections = [];
                        const headings = cleanDesc.match(sectionPattern) || [];
                        if (parts[0]?.trim()) {
                          sections.push({ heading: null, content: parts[0].trim() });
                        }
                        let hIdx = 0;
                        for (let i = 1; i < parts.length; i++) {
                          const text = parts[i]?.trim();
                          if (headings[hIdx] && text.toLowerCase().replace(/[^a-z]/g, '') === headings[hIdx].replace(/[:\s]/g, '').toLowerCase().replace(/[^a-z]/g, '')) {
                            hIdx++;
                            continue;
                          }
                          if (text) {
                            sections.push({ heading: headings[hIdx - 1]?.replace(/[:\s]+$/, '') || null, content: text });
                          }
                        }

                        // If no sections found, split by sentences
                        if (sections.length === 0) {
                          const sentences = desc.split(/(?<=[.!?])\s+/).filter(Boolean);
                          sections.push({ heading: null, content: sentences.join(' ') });
                        }

                        return (
                          <>
                            {/* Inline metadata bar */}
                            {metaItems.length > 0 && (
                              <div className="flex flex-wrap gap-4 pb-3 mb-3 border-b border-gray-100">
                                {metaItems.map((m, i) => (
                                  <span key={i} className="text-sm">
                                    <span className="font-semibold text-text-light-primary dark:text-text-dark-primary">{m.label}:</span>{' '}
                                    <span>{m.value}</span>
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Sections */}
                            {sections.map((sec, i) => (
                              <div key={i} className={sec.heading ? 'pt-2' : ''}>
                                {sec.heading && (
                                  <h4 className="text-base font-semibold text-text-light-primary dark:text-text-dark-primary mb-2">{sec.heading}</h4>
                                )}
                                <div className="space-y-1.5">
                                  {sec.content.split(/[\n•·–—]/).filter(Boolean).map((line, j) => {
                                    const trimmed = line.trim();
                                    if (!trimmed) return null;
                                    const isBullet = /^[-\d]/.test(trimmed) || sec.content.includes('•');
                                    return isBullet ? (
                                      <div key={j} className="flex items-start gap-2 pl-1">
                                        <span className="text-primary mt-1.5 text-xs">&#9679;</span>
                                        <span>{trimmed.replace(/^[-\d.)\s]+/, '')}</span>
                                      </div>
                                    ) : (
                                      <p key={j}>{trimmed}</p>
                                    );
                                  })}
                                </div>
                              </div>
                            ))}
                          </>
                        );
                      })()}
                      
                      {jobPdfUrl && (
                        <div className="pt-4 border-t border-border-light dark:border-border-dark mt-4">
                          <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary mb-2">
                            Job Description PDF
                          </h3>
                          <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary mb-2">
                            You can also review the detailed JD as a PDF. ATS compares your resume PDF directly with this JD PDF.
                          </p>
                          <a
                            href={jobPdfUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-2 text-sm font-medium rounded-lg border border-border-light dark:border-border-dark text-primary hover:bg-primary/5"
                          >
                            <Icon name="description" size={18} className="mr-2" />
                            View JD PDF
                          </a>
                          <div className="mt-4 rounded-lg border border-dashed border-border-light dark:border-border-dark overflow-hidden">
                            <object
                              data={jobPdfUrl}
                              type="application/pdf"
                              className="w-full h-64 md:h-80"
                              aria-label="Job description PDF preview"
                            >
                              <p className="p-4 text-sm text-text-light-secondary dark:text-text-dark-secondary">
                                Unable to preview this PDF in the browser.{' '}
                                <a
                                  href={jobPdfUrl}
                                  download={`${job.title || 'job'}-description.pdf`}
                                  className="text-primary underline"
                                >
                                  Download the JD PDF
                                </a>{' '}
                                to view it locally.
                              </p>
                            </object>
                          </div>
                        </div>
                      )}
                      
                      <div className="pt-4">
                        <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary mb-4">
                          Required Skills
                        </h3>
                        <div className="flex flex-wrap gap-2">
                          {(jobTags.length ? jobTags : ['Collaboration', 'Communication']).map((skill) => (
                            <Badge key={skill} variant="default">
                              {skill}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Right Column: Sidebar */}
                <div className="lg:col-span-1 flex flex-col gap-8">
                  {/* Candidate Application Form or HR Management */}
                  {userRole === 'hr' ? (
                    /* HR Management Card */
                    <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
                      <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary mb-4">
                        Job Management
                      </h3>
                      <div className="flex flex-col gap-3">
                        <Button 
                          size="lg" 
                          variant="secondary" 
                          className="w-full"
                          onClick={() => navigate(`/hr#jobs`)}
                        >
                          <Icon name="edit" size={18} className="mr-2" />
                          Edit Job
                        </Button>
                        <Button 
                          size="lg" 
                          variant={job.status === 'Active' ? 'outline' : 'secondary'}
                          className={`w-full ${job.status === 'Active' ? '' : 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100'}`}
                          onClick={handleToggleJobStatus}
                        >
                          {job.status === 'Active' ? 'Close Job' : 'Activate Job'}
                        </Button>
                        <div className="mt-4 pt-4 border-t border-border-light dark:border-border-dark">
                          <p className="text-sm font-semibold text-text-light-primary dark:text-text-dark-primary mb-2">
                            Status: <Badge variant={job.status === 'Active' ? 'default' : 'secondary'}>{job.status}</Badge>
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    /* Candidate Application Form */
                    <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
                      {/* Credit Cost Display */}
                      <div className="mb-4">
                        <CreditCostBadge 
                          serviceType="job_apply"
                          serviceName="Job Application"
                          size="sm"
                        />
                      </div>
                      
                      <form className="flex flex-col gap-4" onSubmit={handleApply}>
                        <div>
                          <label className="text-sm font-medium text-text-light-primary dark:text-text-dark-primary">
                            Name
                            <input
                              type="text"
                              required
                              value={applyForm.candidateName}
                              onChange={(e) => setApplyForm({ ...applyForm, candidateName: e.target.value })}
                              className="mt-1 w-full rounded-lg border border-border-light dark:border-border-dark bg-transparent px-3 py-2 text-sm"
                            />
                          </label>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-text-light-primary dark:text-text-dark-primary">
                            Email
                            <input
                              type="email"
                              required
                              value={applyForm.candidateEmail}
                              onChange={(e) => setApplyForm({ ...applyForm, candidateEmail: e.target.value })}
                              className="mt-1 w-full rounded-lg border border-border-light dark:border-border-dark bg-transparent px-3 py-2 text-sm"
                            />
                          </label>
                        </div>
                        {/* Resume Summary hidden — resume is fetched from candidate profile */}
                        <div>
                          <label className="text-sm font-medium text-text-light-primary dark:text-text-dark-primary">
                            Cover Letter (optional)
                            <textarea
                              rows="2"
                              value={applyForm.coverLetter}
                              onChange={(e) => setApplyForm({ ...applyForm, coverLetter: e.target.value })}
                              className="mt-1 w-full rounded-lg border border-border-light dark:border-border-dark bg-transparent px-3 py-2 text-sm"
                            />
                          </label>
                        </div>
                        
                        {/* Show different button based on application status */}
                        {existingApplication ? (
                          <div className="space-y-3">
                            {/* Status banner with color coding */}
                            {(() => {
                              const s = existingApplication.status;
                              const statusConfig = {
                                rejected: { bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800', icon: 'cancel', iconColor: 'text-red-600 dark:text-red-400', titleColor: 'text-red-900 dark:text-red-100', subtitleColor: 'text-red-700 dark:text-red-300', label: 'Not Selected', badge: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
                                offer: { bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800', icon: 'check_circle', iconColor: 'text-green-600 dark:text-green-400', titleColor: 'text-green-900 dark:text-green-100', subtitleColor: 'text-green-700 dark:text-green-300', label: 'Selected', badge: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
                                interview_completed: { bg: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-200 dark:border-purple-800', icon: 'hourglass_top', iconColor: 'text-purple-600 dark:text-purple-400', titleColor: 'text-purple-900 dark:text-purple-100', subtitleColor: 'text-purple-700 dark:text-purple-300', label: 'Interview Completed', badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' },
                                completed: { bg: 'bg-purple-50 dark:bg-purple-900/20', border: 'border-purple-200 dark:border-purple-800', icon: 'hourglass_top', iconColor: 'text-purple-600 dark:text-purple-400', titleColor: 'text-purple-900 dark:text-purple-100', subtitleColor: 'text-purple-700 dark:text-purple-300', label: 'Interview Completed', badge: 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300' },
                                review: { bg: 'bg-yellow-50 dark:bg-yellow-900/20', border: 'border-yellow-200 dark:border-yellow-800', icon: 'visibility', iconColor: 'text-yellow-600 dark:text-yellow-400', titleColor: 'text-yellow-900 dark:text-yellow-100', subtitleColor: 'text-yellow-700 dark:text-yellow-300', label: 'Under Review', badge: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300' },
                                interview: { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', icon: 'record_voice_over', iconColor: 'text-blue-600 dark:text-blue-400', titleColor: 'text-blue-900 dark:text-blue-100', subtitleColor: 'text-blue-700 dark:text-blue-300', label: 'Interview Scheduled', badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
                              };
                              const cfg = statusConfig[s] || { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', icon: 'info', iconColor: 'text-blue-600 dark:text-blue-400', titleColor: 'text-blue-900 dark:text-blue-100', subtitleColor: 'text-blue-700 dark:text-blue-300', label: 'Applied', badge: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' };

                              return (
                                <div className={`${cfg.bg} border ${cfg.border} rounded-lg p-4`}>
                                  <div className="flex items-start gap-3">
                                    <Icon name={cfg.icon} size={20} className={`${cfg.iconColor} mt-0.5`} />
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2 mb-1">
                                        <p className={`text-sm font-semibold ${cfg.titleColor}`}>
                                          You've already applied to this position
                                        </p>
                                      </div>
                                      <div className="flex items-center gap-2 mt-1">
                                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${cfg.badge}`}>
                                          {cfg.label}
                                        </span>
                                        {existingApplication.ats_score != null && (
                                          <span className="text-xs text-gray-500 dark:text-gray-400">
                                            ATS Score: {existingApplication.ats_score.toFixed(1)}%
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}

                            {/* Show Continue Assessment button only if ATS >= threshold and interview not completed/rejected/offered */}
                            {existingApplication.ats_score >= (job?.ats_pass_threshold || 50) &&
                             !['interview_completed', 'completed', 'rejected', 'offer'].includes(existingApplication.status) && (
                              <Button
                                size="lg"
                                className="w-full"
                                onClick={() => {
                                  if (job?.live_interview_enabled) {
                                    navigate('/live-interview/welcome', {
                                      state: {
                                        applicationId: existingApplication.id,
                                        jobId: job.id,
                                        jobTitle: job.title || existingApplication.job_title,
                                        candidateName: existingApplication.candidate_name,
                                        candidateId: existingApplication.candidate_id,
                                        duration: job.live_interview_config?.duration_minutes || 5,
                                        continueInterview: true
                                      }
                                    });
                                  } else {
                                    navigate(`/assessment/welcome?candidate_id=${existingApplication.candidate_id}&job_id=${existingApplication.job_id}`, {
                                      state: {
                                        applicationId: existingApplication.id,
                                        jobTitle: existingApplication.job_title,
                                        candidateName: existingApplication.candidate_name,
                                        continueInterview: true
                                      }
                                    });
                                  }
                                }}
                              >
                                <Icon name="play_arrow" size={20} />
                                Continue Assessment
                              </Button>
                            )}

                            {/* Show Resume Not Matched message if ATS < threshold and not already resolved */}
                            {existingApplication.ats_score < (job?.ats_pass_threshold || 50) &&
                             !['rejected', 'interview_completed', 'completed', 'offer'].includes(existingApplication.status) && (
                              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-center">
                                <Icon name="cancel" size={24} className="text-red-600 dark:text-red-400 mx-auto mb-2" />
                                <p className="text-sm font-semibold text-red-800 dark:text-red-200 mb-1">
                                  Resume Not Matched
                                </p>
                                <p className="text-xs text-red-700 dark:text-red-300">
                                  Your ATS score ({existingApplication.ats_score.toFixed(1)}%) is below the minimum threshold ({job?.ats_pass_threshold || 50}%).
                                  Please update your resume to better match the job requirements.
                                </p>
                              </div>
                            )}

                            {/* Show status message if interview completed - under review */}
                            {(existingApplication.status === 'interview_completed' ||
                              existingApplication.status === 'completed') && (
                              <div className="bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg p-3 text-center">
                                <p className="text-sm text-purple-800 dark:text-purple-200">
                                  Your interview is completed. Application is under review by the hiring team.
                                </p>
                              </div>
                            )}

                            {/* Show selection message */}
                            {existingApplication.status === 'offer' && (
                              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-center">
                                <Icon name="celebration" size={24} className="text-green-600 dark:text-green-400 mx-auto mb-1" />
                                <p className="text-sm font-semibold text-green-800 dark:text-green-200">
                                  Congratulations! You have been selected for this position.
                                </p>
                              </div>
                            )}

                            {/* Show rejection message if rejected */}
                            {existingApplication.status === 'rejected' && (
                              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-center">
                                <p className="text-sm text-red-800 dark:text-red-200">
                                  Your application was not selected for this position.
                                </p>
                              </div>
                            )}
                          </div>
                        ) : (
                          <Button size="lg" className="w-full" type="submit" disabled={applyState.loading || checkingApplication}>
                            {checkingApplication ? 'Checking...' : applyState.loading ? 'Submitting...' : 'Apply Now'}
                          </Button>
                        )}
                        
                        {applyState.message && (
                          <div
                            className={`rounded-lg px-3 py-2 text-sm ${
                              applyState.variant === 'error'
                                ? 'border border-red-200 bg-red-50 text-red-700'
                                : 'border border-green-200 bg-green-50 text-green-700'
                            }`}
                          >
                            {applyState.message}
                          </div>
                        )}
                      </form>
                    </div>
                  )}
                  
                  {/* Applications List for HR */}
                  {userRole === 'hr' && (
                    <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
                      <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary mb-4">
                        Applications ({applications.length})
                      </h3>
                      {loadingApplications ? (
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">Loading applications...</p>
                      ) : applications.length === 0 ? (
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">No applications yet.</p>
                      ) : (
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                          {applications.map((app) => (
                            <div key={app.id} className="border border-border-light dark:border-border-dark rounded-lg p-3">
                              <div className="flex items-start justify-between mb-2">
                                <div>
                                  <p className="font-semibold text-sm text-text-light-primary dark:text-text-dark-primary">
                                    {app.candidate_name}
                                  </p>
                                  <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                                    {app.candidate_email}
                                  </p>
                                </div>
                                {app.ats_score !== null && (
                                  <Badge variant="default">{app.ats_score}%</Badge>
                                )}
                              </div>
                              {app.resume_text && (
                                <p className="text-xs text-text-secondary-light dark:text-text-secondary-dark line-clamp-2 mt-2">
                                  {app.resume_text}
                                </p>
                              )}
                              <div className="flex items-center gap-2 mt-2">
                                {app.resume_url ? (
                                  <a
                                    href={app.resume_url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    download
                                    className="text-xs text-primary hover:underline flex items-center gap-1"
                                  >
                                    <Icon name="description" size={14} />
                                    View Resume PDF
                                  </a>
                                ) : (
                                  <span className="text-xs text-text-secondary-light dark:text-text-secondary-dark">
                                    No resume available
                                  </span>
                                )}
                                {app.status && (
                                  <Badge variant="secondary" className="ml-auto">{app.status}</Badge>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {/* Summary Card */}
                  <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
                    <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary mb-4">
                      Job Summary
                    </h3>
                    <ul className="space-y-3">
                      <li className="flex items-center gap-3">
                        <Icon name="work" className="text-text-light-secondary dark:text-text-dark-secondary text-xl" />
                        <span className="text-sm text-text-light-secondary dark:text-text-dark-secondary">
                          Job Type:{' '}
                          <strong className="font-medium text-text-light-primary dark:text-text-dark-primary">
                            {job.type}
                          </strong>
                        </span>
                      </li>
                      <li className="flex items-center gap-3">
                        <Icon name="signal_cellular_alt" className="text-text-light-secondary dark:text-text-dark-secondary text-xl" />
                        <span className="text-sm text-text-light-secondary dark:text-text-dark-secondary">
                          Experience Level:{' '}
                          <strong className="font-medium text-text-light-primary dark:text-text-dark-primary">
                            {job.experience_level}
                          </strong>
                        </span>
                      </li>
                      <li className="flex items-center gap-3">
                        <Icon name="calendar_today" className="text-text-light-secondary dark:text-text-dark-secondary text-xl" />
                        <span className="text-sm text-text-light-secondary dark:text-text-dark-secondary">
                          Date Posted:{' '}
                          <strong className="font-medium text-text-light-primary dark:text-text-dark-primary">
                            {postedDate}
                          </strong>
                        </span>
                      </li>
                    </ul>
                  </div>
                  
                  {/* Company Info Card */}
                  <div className="bg-card-light dark:bg-card-dark rounded-xl shadow-soft p-6">
                    <div className="flex items-center gap-4 mb-4">
                      <Avatar 
                        src={`https://ui-avatars.com/api/?name=${encodeURIComponent(job.company)}&background=0f172a&color=fff`}
                        alt={`${job.company} Logo`}
                        size={12}
                        className="rounded-lg"
                      />
                      <div>
                        <h3 className="text-lg font-bold text-text-light-primary dark:text-text-dark-primary">
                          {job.company}
                        </h3>
                        <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark">
                          {job.location}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-text-light-secondary dark:text-text-secondary-dark leading-relaxed">
                      Our AI-powered system helps hiring teams efficiently review and prioritize applications.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Resume Required Modal */}
      {showResumeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full mx-4 overflow-hidden">
            <div className="p-6 text-center">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="upload_file" size={28} className="text-amber-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 mb-1">Resume Not Found</h2>
              <p className="text-sm text-gray-600 mb-4">
                Please upload your resume in your profile to apply. Our AI uses it to match you with this role.
              </p>
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 mb-4 text-left">
                <ul className="space-y-1.5 text-xs text-teal-700">
                  <li className="flex items-center gap-1.5"><Icon name="check" size={14} className="text-teal-600" /> AI match score & analysis</li>
                  <li className="flex items-center gap-1.5"><Icon name="check" size={14} className="text-teal-600" /> Personalized recommendations</li>
                  <li className="flex items-center gap-1.5"><Icon name="check" size={14} className="text-teal-600" /> Faster application process</li>
                </ul>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowResumeModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowResumeModal(false);
                    navigate('/candidate/dashboard');
                  }}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 flex items-center justify-center gap-1.5"
                >
                  <Icon name="person" size={16} />
                  Go to Profile
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>

      {/* Profile Completion / Credits Modal */}
      {showCreditModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-[#0a1929] to-[#1e3a5f] rounded-2xl shadow-2xl max-w-md w-full p-8 relative border border-blue-500/20">
            {/* Close button */}
            <button
              onClick={() => setShowCreditModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <Icon name="close" size={24} />
            </button>

            {!isProfileComplete ? (
              /* Profile Incomplete - Show Complete Profile Message */
              <>
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="person_add" size={40} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Complete Your Profile</h3>
                  <p className="text-gray-300 text-sm">
                    Get 50 free credits when you complete your profile!
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6 border border-white/20">
                  <div className="flex items-start space-x-3">
                    <Icon name="stars" size={24} className="text-yellow-400 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="text-white font-semibold mb-2">What you'll get:</h4>
                      <ul className="space-y-2 text-sm text-gray-300">
                        <li className="flex items-center space-x-2">
                          <Icon name="check_circle" size={16} className="text-green-400" />
                          <span>50 free credits instantly</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <Icon name="check_circle" size={16} className="text-green-400" />
                          <span>Access to all AI features</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <Icon name="check_circle" size={16} className="text-green-400" />
                          <span>Better job recommendations</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={() => navigate('/candidate/profile/edit')}
                    className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white py-3 rounded-xl font-semibold shadow-lg"
                  >
                    Complete Profile Now
                  </Button>
                  <Button
                    onClick={() => setShowCreditModal(false)}
                    variant="outline"
                    className="w-full border-2 border-white/30 text-white hover:bg-white/10 py-3 rounded-xl"
                  >
                    Maybe Later
                  </Button>
                </div>
              </>
            ) : (
              /* Profile Complete - Redirect to Payment */
              <>
                <div className="text-center mb-6">
                  <div className="w-20 h-20 bg-gradient-to-br from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon name="account_balance_wallet" size={40} className="text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Insufficient Credits</h3>
                  <p className="text-gray-300 text-sm">
                    You need {getCost('job_apply')} credits for Job Application
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-6 border border-white/20">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-300 text-sm">Available Credits:</span>
                    <span className="text-white font-bold text-lg">{balance}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300 text-sm">Required Credits:</span>
                    <span className="text-orange-400 font-bold text-lg">{getCost('job_apply')}</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={() => navigate('/payment')}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-3 rounded-xl font-semibold shadow-lg"
                  >
                    Buy Credits
                  </Button>
                  <Button
                    onClick={() => setShowCreditModal(false)}
                    variant="outline"
                    className="w-full border-2 border-white/30 text-white hover:bg-white/10 py-3 rounded-xl"
                  >
                    Cancel
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default JobDetailsPage;
